<template>
  <div class="input-list">
    <!-- List jerárquico: selector jerárquico (prioridad alta) -->
    <HierarchicalSelect
      v-if="hasHierarchicalOptions"
      :value="value"
      :listOptions="listOptions"
      :listType="field.listType"
      :multiple="!!field.isArray"
      :parentLabel="field.parentLabel || 'Categoría'"
      :childLabel="field.childLabel || 'Subcategoría'"
      :parentPlaceholder="field.parentPlaceholder || ''"
      :childPlaceholder="field.childPlaceholder || ''"
      :levelPlaceholders="field.levelPlaceholders || []"
      :disabled="disabled"
      @input="onInput"
    />

    <!-- List múltiple: multiselect moderno -->
    <div v-else-if="field.listType">
      <multiselect
        :value="value"
        :options="listOptions"
        :multiple="field.isArray"
        :close-on-select="!field.isArray"
        :clear-on-select="true"
        :preserve-search="true"
        :preselect-first="false"
        :allow-empty="true"
        :searchable="true"
        :show-labels="true"
        :taggable="true"
        :create-tag="true"
        label="label"
        track-by="_id"
        :disabled="disabled"
        :placeholder="
          field.isArray
            ? 'Select one or more options or type to add new'
            : 'Select or type to add new...'
        "
        class="mb-2"
        @input="onInput"
        @remove="onInput(null)"
        @tag="onTagAdded"
      >
        <template #selection="{ values, option }">
          <span v-if="field.isArray">
            <span v-if="values.length === 0" class="multiselect__placeholder"
              >Select one or more options</span
            >
            <span v-for="(opt, i) in values" :key="i" class="multiselect__tag">
              {{ opt.label }}
              <span
                class="multiselect__tag-icon"
                @click.stop="onInput(values.filter((o) => o._id !== opt._id))"
              ></span>
            </span>
          </span>
          <span v-else>
            <template v-if="option">
              {{ option.label }}
              <span
                class="multiselect__tag-icon"
                @click.stop="onInput(null)"
              ></span>
            </template>
          </span>
        </template>
      </multiselect>
    </div>

    <!-- List simple: multiselect moderno -->
    <multiselect
      v-else
      :value="value"
      :options="listOptions"
      :multiple="field.isArray"
      :close-on-select="!field.isArray"
      :clear-on-select="true"
      :preserve-search="true"
      :preselect-first="false"
      :allow-empty="true"
      :searchable="true"
      :show-labels="true"
      label="label"
      track-by="_id"
      :disabled="disabled"
      :placeholder="
        field.isArray ? 'Select one or more options' : 'Select an option'
      "
      class="mb-2"
      @input="onInput"
      @remove="onInput(null)"
    >
      <template #selection="{ values, option }">
        <span v-if="field.isArray">
          <span v-if="values.length === 0" class="multiselect__placeholder"
            >Select one or more options</span
          >
          <span v-for="(opt, i) in values" :key="i" class="multiselect__tag">
            {{ opt.label }}
            <span
              class="multiselect__tag-icon"
              @click.stop="onInput(values.filter((o) => o._id !== opt._id))"
            ></span>
          </span>
        </span>
        <span v-else>
          <template v-if="option">
            {{ option.label }}
            <span
              class="multiselect__tag-icon"
              @click.stop="onInput(null)"
            ></span>
          </template>
        </span>
      </template>
    </multiselect>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import HierarchicalSelect from "../HierarchicalSelect.vue";

export default {
  name: "InputList",
  components: {
    Multiselect,
    HierarchicalSelect,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Boolean, Object, Array],
      default: null,
    },
    fieldId: {
      type: String,
      required: true,
    },
    listOptions: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasHierarchicalOptions() {
      if (!this.listOptions || this.listOptions.length === 0) return false;

      // Verificar si hay elementos con parent (hijos) y elementos sin parent (padres)
      const hasParents = this.listOptions.some((item) => !item.parent);
      const hasChildren = this.listOptions.some((item) => item.parent);

      return hasParents && hasChildren;
    },
  },
  methods: {
    onInput(value) {
      this.$emit("input", value);
    },
    onTagAdded(tag) {
      // Crear un nuevo objeto con la estructura esperada
      const newTag = {
        _id: tag,
        label: tag,
      };

      // Si es un array, agregar al array existente
      if (this.field.isArray) {
        const currentValue = Array.isArray(this.value) ? this.value : [];
        const newValue = [...currentValue, newTag];
        this.onInput(newValue);
      } else {
        // Si no es array, reemplazar el valor
        this.onInput(newTag);
      }
    },
  },
};
</script>

<style scoped>
.input-list {
  width: 100%;
}

/* Estilos para multiselect */
:deep(.multiselect-container) {
  position: relative;
  z-index: 1;
}

:deep(.multiselect.is-active) {
  z-index: 99999;
}

:deep(.multiselect__content-wrapper) {
  top: 100% !important;
  bottom: auto !important;
  transform: none !important;
  z-index: 99999 !important;
  position: absolute !important;
}

:deep(.multiselect__content) {
  max-height: 200px;
  overflow-y: auto;
}
</style>
